import {
  parseAsArrayOf,
  parseAsBook,
  parseAsClassification,
  parseAsParty,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from '@vrplatform/query-params';
import { hasValue } from '@vrplatform/utils';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { journalEntryType_enum } from '@vrplatform/graphql';
import type { JournalEntryRelation } from '@vrplatform/ui-common';
import type { RequestQuery } from '@vrplatform/data';
import {
  useJournalEntriesModalFilter,
  useJournalEntriesModalWhere,
} from './useJournalEntriesModal';

const PUBLISHED = ['draft', 'published'] as const;

const RELATIONS: JournalEntryRelation[] = [
  'reservation',
  'deposit',
  'expense',
  'recurringFee',
  'transfer',
];

const subTypes = Object.keys({
  reservation: undefined,
  reservation_adjustment: undefined,
  reservation_adjustment_cancellation: undefined,
  reservation_fee: undefined,
  reservation_fee_adjustment: undefined,
  reservation_fee_tax: undefined,
  reservation_line: undefined,
  transaction_deposit: undefined,
  transaction_deposit_channelFee: undefined,
  transaction_deposit_line: undefined,
  transaction_deposit_merchantFee: undefined,
  transaction_deposit_openingAdvance: undefined,
  transaction_deposit_openingBalance: undefined,
  transaction_deposit_refund: undefined,
  transaction_deposit_reserve: undefined,
  transaction_deposit_vat: undefined,
  transaction_expense: undefined,
  transaction_expense_line: undefined,
  transaction_expense_markup: undefined,
  transaction_expense_markup_tax: undefined,
  transaction_expense_payment: undefined,
  transaction_transfer: undefined,
  transaction_transfer_line: undefined,
  transaction_deposit_coHost: undefined,
  transaction_payout: undefined,
  transaction_payout_line: undefined,
} satisfies Record<
  journalEntryType_enum,
  undefined
>) as journalEntryType_enum[];

type OpeningBalanceType = NonNullable<
  RequestQuery<'get:/reports/journal-entries'>['openingBalanceType']
>;
const openingBalanceTypes = Object.keys({
  taxPayable: undefined,
  advancedDeposit: undefined,
  listingOpeningBalance: undefined,
} satisfies Record<OpeningBalanceType, undefined>) as OpeningBalanceType[];

export const useJournalEntriesTableFilter = () => {
  'use memo';

  const [queryParams, setQueryParams] = useQueryStates({
    search: parseAsString,
    amount: parseAsString,
    listingIds: parseAsArrayOf(parseAsString),
    listingOwnershipPeriodIds: parseAsArrayOf(parseAsString),
    contactIds: parseAsArrayOf(parseAsString),
    bookingChannels: parseAsArrayOf(parseAsString),
    reservationIds: parseAsArrayOf(parseAsString),
    party: parseAsParty,
    type: parseAsStringLiteral(RELATIONS),
    subType: parseAsArrayOf(parseAsStringLiteral(subTypes)),
    date: parseAsString,
    published: parseAsStringLiteral(PUBLISHED),
    accountIds: parseAsArrayOf(parseAsString),
    classifications: parseAsArrayOf(parseAsClassification),
    currency: parseAsString,
    view: parseAsString,
    ledger: parseAsBook,
    status: parseAsStringLiteral([
      'active',
      'inactive',
      'all' as const,
    ]).withDefault('active'),
    openingBalanceType: parseAsStringLiteral(openingBalanceTypes),
  });

  const goto = useNavigate();
  const { filter: state, setFilter: setState } = useJournalEntriesModalFilter();
  const where = useJournalEntriesModalWhere();
  const isModalOpened = !!where;

  const isFiltered = isModalOpened
    ? Object.values((state as object) ?? {}).some(hasValue)
    : Object.values(queryParams).some(hasValue);

  const gotoDetailView = useCallback(
    (filter: Partial<typeof state>) => {
      const search = new URLSearchParams();

      for (const [key, value] of Object.entries(filter)) {
        if (value) {
          search.set(key, Array.isArray(value) ? value.join(',') : value);
        }
      }

      goto(`/reports/detail?${search.toString()}`);
    },
    [goto]
  );

  return {
    filter: isModalOpened ? (state as typeof queryParams) : queryParams,
    isFiltered,
    gotoDetailView,
    setFilter: (filter: Partial<typeof state>) => {
      if (isModalOpened) {
        setState(filter);
      } else {
        setQueryParams(filter as Partial<typeof queryParams>);
      }
    },
    reset: () => {
      const initial: Record<keyof typeof queryParams, undefined> = {
        contactIds: undefined,
        reservationIds: undefined,
        search: undefined,
        listingIds: undefined,
        party: undefined,
        date: undefined,
        type: undefined,
        amount: undefined,
        subType: undefined,
        classifications: undefined,
        accountIds: undefined,
        status: undefined,
        published: undefined,
        currency: undefined,
        view: undefined,
        ledger: undefined,
        bookingChannels: undefined,
        openingBalanceType: undefined,
        listingOwnershipPeriodIds: undefined,
      };

      if (isModalOpened) {
        setState(initial);
      } else {
        setQueryParams(null);
      }
    },
  };
};
